import { useEffect, useState } from 'react';
import { Landmark, Vote, Check, Clock3, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { api, post } from './api';
import { text, type Locale } from '../shared/content';
import type { Candidate, Government } from '../shared/types';
import { errorText } from './i18n';
const copy = {
  paused: text('Elections temporarily paused', '选举暂时暂停', '選舉暫時暫停', 'Elecciones en pausa'),
  vacancyElection: text(
    'Vacancy election · five days',
    '空缺补选 · 五天',
    '空缺補選 · 五天',
    'Elección de vacantes · cinco días',
  ),
  regularElection: text('Regular city election', '定期城市选举', '定期城市選舉', 'Elección ordinaria'),
  withdraw: text('Withdraw candidacy', '撤回参选', '撤回參選', 'Retirar candidatura'),
  checkIn: text(
    'Review the city desk',
    '查看并确认市政值守',
    '查看並確認市政值守',
    'Revisar asuntos municipales',
  ),
  search: text('Find a candidate', '查找候选人', '尋找候選人', 'Buscar candidato'),
  more: text('Show more candidates', '查看更多候选人', '查看更多候選人', 'Ver más candidatos'),
  title: text(
    'Fictional in-game government',
    '虚构的游戏内政府',
    '虛構的遊戲內政府',
    'Gobierno ficticio del juego',
  ),
  rules: text(
    'Voting requires a verified email, 72 hours of residency, two active dates, and the first two story goals. Candidates need four active dates. Elections need 30 eligible residents; mayor ballots also need two mayor candidates. Council seats are elected citywide; districts describe service areas. Council-only vacancies do not require mayor candidates.',
    '投票需验证邮箱、居住满 72 小时、在两个日期实际游玩并完成前两段目标。参选需四个活跃日期。需 30 位合格居民；市长选票还需两名市长候选人。议员由全城直选，区域为服务范围；仅议员补选不要求市长候选人。',
    '投票需驗證信箱、居住滿 72 小時、在兩個日期實際遊玩並完成前兩段目標。參選需四個活躍日期。需 30 位合格居民；市長選票還需兩名市長候選人。議員由全城直選，區域為服務範圍；僅議員補選不要求市長候選人。',
    'Se exige correo verificado, 72 h de residencia, dos días activos y los dos primeros objetivos. Candidatos: cuatro días activos. Se necesitan 30 electores y, para la alcaldía, dos candidatos. Los concejales se eligen en toda la ciudad; sus áreas indican a quién atienden. Sus vacantes no exigen candidatos a alcalde.',
  ),
  nomination: text('Candidate registration', '候选人报名', '候選人報名', 'Inscripción'),
  campaign: text('Meet the candidates', '认识候选人', '認識候選人', 'Conoce a los candidatos'),
  voting: text('Voting is open', '投票进行中', '投票進行中', 'Votación abierta'),
  governance: text('Community growth', '社区成长阶段', '社區成長階段', 'Crecimiento comunitario'),
  handover: text('Counting & handover', '计票与交接', '計票與交接', 'Recuento y relevo'),
  settled: text('Results recorded', '结果已记录', '結果已記錄', 'Resultados registrados'),
  mayor: text('Mayor', '市长', '市長', 'Alcalde'),
  brooklyn: text('Brooklyn councilor', '布鲁克林议员', '布魯克林議員', 'Concejal de Brooklyn'),
  midtown: text('Midtown councilor', '中城议员', '中城議員', 'Concejal de Midtown'),
  lower: text('Lower Manhattan councilor', '下城议员', '下城議員', 'Concejal del Bajo Manhattan'),
  vacant: text(
    'Caretaker services · vacant seat',
    '托管服务 · 席位空缺',
    '託管服務 · 席位空缺',
    'Servicios provisionales · vacante',
  ),
  register: text('Enter the election', '报名参选', '報名參選', 'Presentar candidatura'),
  statement: text(
    'Your neighborhood promise · 10–500 characters',
    '你对街区的承诺 · 10–500 字',
    '你對街區的承諾 · 10–500 字',
    'Tu compromiso con el barrio · 10–500 caracteres',
  ),
  vote: text('Cast secret ballot', '投出秘密选票', '投出秘密選票', 'Votar en secreto'),
  voted: text('Your vote is recorded', '你的选票已记录', '你的選票已記錄', 'Tu voto está registrado'),
  confirm: text(
    'Confirm this ballot? Your selection cannot be changed after submission.',
    '确认投票？提交后无法更改选择。',
    '確認投票？送出後無法更改選擇。',
    '¿Confirmar? No podrás cambiar tu voto.',
  ),
  yes: text('Confirm vote', '确认投票', '確認投票', 'Confirmar voto'),
  cancel: text('Cancel', '取消', '取消', 'Cancelar'),
  treasury: text('City treasury', '市政国库', '市政國庫', 'Tesorería'),
  noCandidates: text(
    'No candidates yet. The city still runs on caretaker rules.',
    '目前暂无候选人，城市仍按托管规则正常运行。',
    '目前暫無候選人，城市仍按託管規則正常運行。',
    'Aún no hay candidatos. La ciudad sigue con reglas provisionales.',
  ),
  policy: text('Public policy', '公共政策', '公共政策', 'Política pública'),
  tax: text('Commercial tax', '商业税率', '商業稅率', 'Impuesto comercial'),
  transit: text('Transit support', '交通支持', '交通支援', 'Apoyo al transporte'),
  propose: text('Submit proposal', '提出提案', '提出提案', 'Presentar propuesta'),
  approve: text('Approve', '赞成', '贊成', 'Aprobar'),
  reject: text('Reject', '反对', '反對', 'Rechazar'),
  sign: text('Sign policy', '签署政策', '簽署政策', 'Firmar política'),
  veto: text('Veto', '否决', '否決', 'Vetar'),
  resign: text('Resign this office', '辞去公职', '辭去公職', 'Renunciar al cargo'),
  policyRules: text(
    'Two councilors approve, then the mayor signs. A veto needs three fresh council approvals to override. Policies take effect after five minutes. Officials cannot move treasury funds to personal wallets.',
    '两名议员同意后由市长签署。否决后需三名议员重新一致表决才能推翻。政策五分钟后生效。官员不能将国库资金转入私人钱包。',
    '兩名議員同意後由市長簽署。否決後需三名議員重新一致表決才能推翻。政策五分鐘後生效。官員不能將國庫資金轉入私人錢包。',
    'Dos concejales aprueban y el alcalde firma. Revocar un veto exige tres votos nuevos. Las políticas entran en vigor en cinco minutos. No se permiten transferencias de tesorería a cuentas personales.',
  ),
  absence: text(
    'Seats vacate after seven days without official action. Basic services continue. A tie or fewer than three votes leaves a seat vacant. A five-day vacancy election can run when there is time before regular nominations; otherwise vacancies wait for the regular election. NPCs never vote. Two successive terms require a break of at least 21 days.',
    '官员连续七日无履职操作将自动腾出席位，基础服务继续。平票或不足三票时席位空缺，定期报名开始前有足够时间时，会开启五天补选，否则等定期选举。NPC 不投票。连续两任后须间隔至少 21 天。',
    '官員連續七日無履職操作將自動騰出席位，基礎服務繼續。平票或不足三票時席位空缺，定期報名開始前有足夠時間時，會開啟五天補選，否則等定期選舉。NPC 不投票。連續兩任後須間隔至少 21 天。',
    'Los cargos se liberan tras siete días sin actividad oficial. Los servicios continúan. Un empate o menos de tres votos deja vacante el cargo hasta una elección de cinco días si hay tiempo antes de la ordinaria. Los NPC no votan. Tras dos mandatos seguidos se exigen al menos 21 días de pausa.',
  ),
  saved: text('Recorded.', '已记录。', '已記錄。', 'Registrado.'),
};
export default function GovernmentPanel({
  gov,
  locale,
  characterId,
  onRefresh,
  onMessage,
}: {
  gov: Government | null;
  locale: Locale;
  characterId: string;
  onRefresh: () => Promise<void>;
  onMessage: (s: string, error?: boolean) => void;
}) {
  const t = (k: keyof typeof copy) => copy[k][locale];
  const [busy, setBusy] = useState(false),
    [office, setOffice] = useState('mayor'),
    [statement, setStatement] = useState(''),
    [candidate, setCandidate] = useState<{ id: string; name: string; office: string } | null>(null),
    [kind, setKind] = useState('tax'),
    [value, setValue] = useState(8);
  const [extra, setExtra] = useState<Candidate[]>([]),
    [query, setQuery] = useState(''),
    [searchResult, setSearchResult] = useState<{ candidates: Candidate[]; total: number } | null>(null);
  useEffect(() => {
    setExtra([]);
    setSearchResult(null);
    setQuery('');
    setCandidate(null);
    setOffice('mayor');
  }, [gov?.electionId]);
  if (!gov) return <p className="muted">…</p>;
  const availableOffices =
    gov.electionScope === 'district'
      ? gov.electionOffices.filter((o) => o === 'mayor' || o === (gov.district || 'brooklyn'))
      : gov.electionOffices;
  const selectedOffice = availableOffices.includes(office) ? office : availableOffices[0];
  const visibleCandidates = searchResult?.candidates || [
    ...new Map([...gov.candidates, ...extra].map((c) => [c.characterId, c])).values(),
  ];
  const total = searchResult?.total ?? gov.candidatesTotal;
  const findCandidates = async (more = false) => {
    setBusy(true);
    try {
      const page = await api<{ candidates: Candidate[]; total: number }>(
        `/candidates?electionId=${encodeURIComponent(gov.electionId)}&offset=${more ? visibleCandidates.length : 0}&query=${encodeURIComponent(query)}`,
      );
      if (query || searchResult)
        setSearchResult({
          candidates: more ? [...visibleCandidates, ...page.candidates] : page.candidates,
          total: page.total,
        });
      else setExtra(more ? [...extra, ...page.candidates] : page.candidates);
    } catch (e) {
      onMessage(errorText(locale, e), true);
    } finally {
      setBusy(false);
    }
  };
  const own = gov.seats.find((s) => s.characterId === characterId);
  const call = async (path: string, body: unknown) => {
    setBusy(true);
    try {
      await post(path, body);
      await onRefresh();
      onMessage(t('saved'));
      return true;
    } catch (e) {
      onMessage(errorText(locale, e), true);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="government-panel">
      <div className="fictional-label">
        <ShieldCheck size={12} />
        {t('title')}
      </div>
      <div className="election-phase">
        <Clock3 size={15} />
        <strong>
          {t((gov.electionPhase in copy ? gov.electionPhase : 'governance') as keyof typeof copy)}
        </strong>
        <small>
          {new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(new Date(gov.endsAt))}
        </small>
      </div>
      <div className="fictional-label">
        {t(gov.electionKind === 'vacancy' ? 'vacancyElection' : 'regularElection')}
      </div>
      <p className="muted small">{t('rules')}</p>
      <div className="treasury">
        <Landmark size={19} />
        <span>{t('treasury')}</span>
        <b>C${gov.treasury.toLocaleString()}</b>
      </div>
      <div className="seat-list">
        {gov.seats.map((s) => (
          <div key={s.office}>
            <span>{t(s.office as 'mayor')}</span>
            <strong>{s.name || t('vacant')}</strong>
          </div>
        ))}
      </div>
      <p className="muted small">{t('absence')}</p>
      {gov.canRun && (
        <form
          className="candidacy-form"
          onSubmit={async (e) => {
            e.preventDefault();
            await call('/candidates', { office: selectedOffice, statement });
          }}
        >
          <h3>{t('register')}</h3>
          <select value={selectedOffice} onChange={(e) => setOffice(e.target.value)}>
            {availableOffices.map((o) => (
              <option key={o} value={o}>
                {t(o as 'mayor')}
              </option>
            ))}
          </select>
          <textarea
            aria-label={t('statement')}
            placeholder={t('statement')}
            minLength={10}
            maxLength={500}
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
          />
          <button className="button primary full" disabled={busy || statement.trim().length < 10}>
            {t('register')}
            <ArrowUpRight size={14} />
          </button>
        </form>
      )}
      {gov.ownCandidate && ['nomination', 'campaign'].includes(gov.electionPhase) && (
        <button
          className="button light full"
          disabled={busy}
          onClick={() => call('/candidates/withdraw', {})}
        >
          {t('withdraw')} · {gov.ownCandidate.name}
        </button>
      )}
      <h3>{t('campaign')}</h3>
      {gov.candidatesTotal > 20 && (
        <form
          className="candidate-search"
          onSubmit={(e) => {
            e.preventDefault();
            findCandidates();
          }}
        >
          <input
            aria-label={t('search')}
            placeholder={t('search')}
            value={query}
            maxLength={50}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!e.target.value) setSearchResult(null);
            }}
          />
          <button className="button soft compact" disabled={busy}>
            {t('search')}
          </button>
        </form>
      )}
      {visibleCandidates.length ? (
        visibleCandidates.map((c) => (
          <article className="candidate-card" key={c.characterId}>
            <span className="post-avatar">{c.name[0]}</span>
            <div>
              <strong>{c.name}</strong>
              <small>{t(c.office as 'mayor')}</small>
              <p>{c.statement}</p>
              {gov.ballots?.find((b) => b.office === c.office) &&
                !gov.ballots.find((b) => b.office === c.office)!.hasVoted &&
                gov.electionPhase === 'voting' && (
                  <button
                    className="button soft compact"
                    onClick={() => setCandidate({ id: c.characterId, name: c.name, office: c.office })}
                  >
                    <Vote size={13} />
                    {t('vote')}
                  </button>
                )}
            </div>
          </article>
        ))
      ) : (
        <p className="muted small">{t('noCandidates')}</p>
      )}
      {visibleCandidates.length < total && (
        <button className="button light full" disabled={busy} onClick={() => findCandidates(true)}>
          {t('more')} · {visibleCandidates.length}/{total}
        </button>
      )}
      {gov.ballots
        ?.filter((b) => b.hasVoted)
        .map((b) => (
          <div className="inline-tip" key={b.office}>
            <Check size={14} />
            {t(b.office as 'mayor')} · {t('voted')}
          </div>
        ))}
      <h3>{t('policy')}</h3>
      <p className="muted small">{t('policyRules')}</p>
      {own?.office === 'mayor' && (
        <div className="policy-form">
          <select
            aria-label={t('policy')}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setValue(e.target.value === 'tax' ? 8 : 0);
            }}
          >
            <option value="tax">{t('tax')}</option>
            <option value="transit">{t('transit')}</option>
          </select>
          <select aria-label="Policy level" value={value} onChange={(e) => setValue(Number(e.target.value))}>
            {(kind === 'tax' ? [4, 6, 8, 10, 12] : [0, 1, 2]).map((n) => (
              <option key={n} value={n}>
                {n}
                {kind === 'tax' ? '%' : ''}
              </option>
            ))}
          </select>
          <button
            className="button primary full"
            disabled={busy}
            onClick={() => call('/civic', { type: 'propose', payload: { kind, value } })}
          >
            {t('propose')}
          </button>
        </div>
      )}
      {gov.proposals.map((proposal) => (
        <article className="policy-proposal" key={proposal.id}>
          <strong>
            {t(proposal.kind as 'tax')} · {proposal.value}
            {proposal.kind === 'tax' ? '%' : ''}
          </strong>
          <small>{proposal.status}</small>
          <div className="council-votes">
            {['brooklyn', 'midtown', 'lower'].map((o) => (
              <span
                key={o}
                className={proposal.votes[o] === true ? 'yes' : proposal.votes[o] === false ? 'no' : ''}
              >
                {t(o as 'brooklyn')}{' '}
                {proposal.votes[o] === true ? '✓' : proposal.votes[o] === false ? '×' : '—'}
              </span>
            ))}
          </div>
          {own &&
            own.office !== 'mayor' &&
            ['voting', 'awaiting_signature', 'vetoed'].includes(proposal.status) && (
              <div className="vote-buttons">
                <button
                  className="button soft compact"
                  disabled={busy}
                  onClick={() =>
                    call('/civic', {
                      type: 'council-vote',
                      payload: { proposalId: proposal.id, approve: true },
                    })
                  }
                >
                  {t('approve')}
                </button>
                <button
                  className="button light compact"
                  disabled={busy}
                  onClick={() =>
                    call('/civic', {
                      type: 'council-vote',
                      payload: { proposalId: proposal.id, approve: false },
                    })
                  }
                >
                  {t('reject')}
                </button>
              </div>
            )}
          {own?.office === 'mayor' && proposal.status === 'awaiting_signature' && (
            <div className="vote-buttons">
              <button
                className="button soft compact"
                disabled={busy}
                onClick={() =>
                  call('/civic', { type: 'sign', payload: { proposalId: proposal.id, approve: true } })
                }
              >
                {t('sign')}
              </button>
              <button
                className="button light compact"
                disabled={busy}
                onClick={() =>
                  call('/civic', { type: 'sign', payload: { proposalId: proposal.id, approve: false } })
                }
              >
                {t('veto')}
              </button>
            </div>
          )}
        </article>
      ))}
      {own && (
        <button
          className="button soft full"
          disabled={busy}
          onClick={() => call('/civic', { type: 'check-in', payload: {} })}
        >
          {t('checkIn')}
        </button>
      )}
      {own && (
        <button
          className="button danger full"
          disabled={busy}
          onClick={() => call('/civic', { type: 'resign', payload: {} })}
        >
          {t('resign')}
        </button>
      )}
      {candidate && (
        <div className="modal-scrim">
          <section className="modal">
            <div className="fictional-label">{t('title')}</div>
            <h2>{candidate.name}</h2>
            <p>{t('confirm')}</p>
            <button
              className="button primary full"
              disabled={busy}
              onClick={async () => {
                if (
                  await call('/vote', {
                    electionId: gov.electionId,
                    office: candidate.office,
                    candidateId: candidate.id,
                  })
                )
                  setCandidate(null);
              }}
            >
              <Vote size={15} />
              {t('yes')}
            </button>
            <button className="button light full" disabled={busy} onClick={() => setCandidate(null)}>
              {t('cancel')}
            </button>
          </section>
        </div>
      )}
    </section>
  );
}
